import type { EditorRequestOp, SnapshotEntity, SnapshotFilter, TraceConfig, TraceEvent } from '@ai-studio/shared';

import { getView, type EngineView } from '../engine/prism.js';
import { useConsole } from '../state/console.js';
import { useGameTrace } from '../state/game-trace.js';
import { usePlay } from '../state/play.js';
import { useScene } from '../state/scene.js';
import { request } from './api.js';
import { summarizeProfile } from './profile-summary.js';
import { snapshotFromLive, trimOutbox, wireEvent } from './game-trace.js';
import { eventSocket } from './ws.js';

/**
 * Streams the running Play window's game trace to the server, and answers the
 * server's live questions (world snapshot, one entity, trace config) so the
 * assistant can see everything that happens in the game without screenshots.
 *
 * - Every 250 ms while Play runs: `view.traceTake(lastSeq)` → Game console
 *   ring buffer + upload queue.
 * - About once a second: one POST /projects/:id/play-trace batch (≤ 4000
 *   events, null fields stripped), an empty heartbeat every 3 s so the server
 *   knows Play is still running, and a final `stop` batch.
 * - A new engine view (Play restart) is a new session.
 */

const POLL_MS = 250;
const UPLOAD_EVERY = 4;
const HEARTBEAT_MS = 3000;
const BATCH_MAX = 4000;
/** Unsent events kept per run while the server is unreachable. */
const OUTBOX_CAP = 20_000;

interface Segment {
  readonly session: string;
  readonly scene: string | null;
  readonly level: string;
  events: TraceEvent[];
  dropped: number;
  startSent: boolean;
  stop: boolean;
  unsupported: boolean;
}

interface Run {
  readonly view: EngineView;
  readonly segment: Segment;
  lastSeq: number;
  configured: boolean;
  /** A trace pull is waiting on the engine. */
  taking: boolean;
}

function sessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function isActive(state: string): boolean {
  return state === 'playing' || state === 'paused';
}

/** Answer one live-editor request from the server. Throws `not_playing` / `unsupported` codes. */
export async function answerEditorRequest(
  op: EditorRequestOp,
  args: Record<string, unknown>,
  view: EngineView | null = getView('play'),
  playing: boolean = isActive(usePlay.getState().state),
): Promise<unknown> {
  if (op === 'traceConfig') {
    const config = (args.config ?? args) as TraceConfig;
    useGameTrace.getState().setConfig(config);
    const applied = Boolean(view && playing && view.supportsTrace && view.traceConfig(config));
    return { applied };
  }
  if (!view || !playing) throw new Error('not_playing: Play is not running in the editor.');
  if (op === 'worldSnapshot') {
    const filter = args as SnapshotFilter;
    if (view.supportsWorldSnapshot) return (await view.worldSnapshot(filter)) ?? { entities: [] };
    const tree = await view.liveTree();
    if (!tree) throw new Error('unsupported: this engine build has no world snapshot (pnpm engine:build).');
    // Older bundles: fetch the entities the snapshot reads, then build it.
    const wanted: string[] = [];
    snapshotFromLive(tree, (id) => (wanted.push(id), null), filter);
    const entities = new Map(await Promise.all(wanted.map(async (id) => [id, (await view.liveEntity(id)) as SnapshotEntity | null] as const)));
    return snapshotFromLive(tree, (id) => entities.get(id) ?? null, filter);
  }
  if (op === 'entityState') {
    const id = typeof args.id === 'string' ? args.id : '';
    if (view.supportsWorldSnapshot) return view.entityState(id);
    if (!view.hasPlayTools) throw new Error('unsupported: this engine build has no entity inspection (pnpm engine:build).');
    return view.liveEntity(id);
  }
  if (op === 'profile') {
    if (!view.supportsProfiler) throw new Error('unsupported: this engine build has no profiler (pnpm engine:build).');
    const frames = Math.max(5, Math.min(600, Math.floor(Number(args.frames) || 120)));
    const was = await view.profiler({ record: true, gpu: true });
    if (args.clear === true || !was?.recording) await view.profiler({ record: true, clear: true, gpu: true, capacity: 600 });
    let result = await view.profilerFrames(-1, frames);
    if (!result || result.frames.length < Math.min(frames, 30)) {
      await new Promise((r) => setTimeout(r, 1500));
      result = await view.profilerFrames(-1, frames);
    }
    const summary = summarizeProfile(result?.frames ?? [], Math.max(1, Math.min(40, Math.floor(Number(args.top) || 12))));
    return { summary, gpu: result?.status?.gpu ?? null };
  }
  throw new Error(`Unknown editor request: ${String(op)}`);
}

export function startPlayTraceSync(projectId: number): () => void {
  const controller = new AbortController();
  const segments: Segment[] = [];
  let run: Run | null = null;
  let ticks = 0;
  let uploading = false;
  let lastSent = 0;

  const trace = () => useGameTrace.getState();

  /** Pulls the run's new events; one pull at a time per run (the engine may be busy). */
  const take = async (r: Run): Promise<void> => {
    if (!r.view.alive || !r.view.supportsTrace || r.taking) return;
    r.taking = true;
    let events: TraceEvent[] | null;
    try {
      events = await r.view.traceTake(r.lastSeq);
    } finally {
      r.taking = false;
    }
    if (!events || events.length === 0) return;
    for (const e of events) if (e.seq > r.lastSeq) r.lastSeq = e.seq;
    trace().append(events);
    r.segment.events.push(...events);
    if (r.segment.events.length > OUTBOX_CAP) {
      const { kept, dropped } = trimOutbox(r.segment.events, OUTBOX_CAP);
      r.segment.events = kept;
      r.segment.dropped += dropped;
      trace().addUnsent(dropped);
    }
  };

  const endRun = () => {
    const ended = run;
    if (!ended) return;
    run = null;
    trace().setSession(null);
    // The last events, then the segment is closed and sent.
    void take(ended).finally(() => {
      ended.segment.stop = true;
      void upload();
    });
  };

  const beginRun = (view: EngineView) => {
    const segment: Segment = {
      session: sessionId(),
      scene: useScene.getState().path ?? null,
      level: trace().config.level,
      events: [],
      dropped: 0,
      startSent: false,
      stop: false,
      unsupported: !view.supportsTrace,
    };
    segments.push(segment);
    // Follows the Console's "Clear on Play".
    if (useConsole.getState().prefs.clearOnPlay) trace().clear();
    // Engine seq starts at 0 for a new world; -1 takes everything.
    run = { view, segment, lastSeq: 0, configured: false, taking: false };
    trace().setSession(segment.session);
  };

  const poll = () => {
    const state = usePlay.getState().state;
    if (!isActive(state)) {
      endRun();
      return;
    }
    const view = getView('play');
    // A different engine view is a new world (restart): close the old run.
    if (run && run.view !== view) endRun();
    if (!view) return;
    if (!run) beginRun(view);
    const current = run!;
    if (!view.supportsTrace) {
      trace().setSupport('unsupported');
      current.segment.unsupported = true;
      return;
    }
    trace().setSupport('supported');
    if (!current.configured) {
      view.traceConfig(trace().config);
      current.configured = true;
    }
    void take(current);
  };

  const upload = async (): Promise<void> => {
    if (uploading) return;
    uploading = true;
    try {
      while (segments.length > 0) {
        const seg = segments[0]!;
        const events = seg.events.slice(0, BATCH_MAX);
        const last = events.length === seg.events.length;
        const stop = seg.stop && last;
        const now = Date.now();
        if (events.length === 0 && !stop && seg.startSent && now - lastSent < HEARTBEAT_MS) break;
        await request(
          'POST',
          `/projects/${projectId}/play-trace`,
          {
            session: seg.session,
            ...(seg.startSent ? {} : { start: { scene: seg.scene, level: seg.level } }),
            ...(stop ? { stop: true } : {}),
            ...(seg.dropped ? { dropped: seg.dropped } : {}),
            ...(seg.unsupported ? { unsupported: true } : {}),
            events: events.map(wireEvent),
          },
          AbortSignal.any([controller.signal, AbortSignal.timeout(8000)]),
        );
        seg.events.splice(0, events.length);
        seg.startSent = true;
        seg.dropped = 0;
        lastSent = now;
        if (stop) segments.shift();
        else if (seg.events.length === 0) break;
      }
    } catch {
      // Kept for the next tick (bounded by OUTBOX_CAP). Not logged: the
      // console would feed itself while the server is unreachable.
    } finally {
      uploading = false;
    }
  };

  const timer = setInterval(() => {
    poll();
    ticks += 1;
    if (ticks % UPLOAD_EVERY === 0) void upload();
  }, POLL_MS);

  // Stop is reported before React tears the view down: take the last events now.
  const unsubscribePlay = usePlay.subscribe((s, prev) => {
    if (prev.state !== s.state && !isActive(s.state) && s.state !== 'starting') endRun();
    else if (prev.runId !== s.runId) endRun();
  });

  // Keep the running game's config in sync with the level picker.
  const unsubscribeConfig = useGameTrace.subscribe((s, prev) => {
    if (s.config !== prev.config && run && run.view.alive && run.view.supportsTrace) run.view.traceConfig(s.config);
  });

  const unsubscribeSocket = eventSocket.subscribe((event) => {
    if (event.type !== 'editor.request' || event.projectId !== projectId) return;
    answerEditorRequest(event.op, event.args).then(
      (result) => eventSocket.respond(event.requestId, { ok: true, result: result ?? null }),
      (error: unknown) =>
        eventSocket.respond(event.requestId, { ok: false, error: error instanceof Error ? error.message : String(error) }),
    );
  });

  return () => {
    endRun();
    clearInterval(timer);
    unsubscribePlay();
    unsubscribeConfig();
    unsubscribeSocket();
    // One last best-effort flush, then cancel anything still in flight.
    void upload().finally(() => controller.abort());
  };
}
